import type { FindingActionKind } from "../../types";
import {
  actionMap,
  findingId,
  noteMap,
  unaddressedFailCount,
  type QaSection,
} from "../../lib/findings";
import FindingMarker from "./FindingMarker";
import FindingContextCard from "./FindingContextCard";

// QA findings review panel (Round 10.1, 2B): the needs-attention QA line
// opens THIS — a clearly labeled list of every active finding from the
// case-level validation scans (summary + costs, the ones that gate
// exports), each with its description, affected fact/amount context, and
// the existing dismiss/accept/flag controls (FindingMarker — the same row
// implementation the costs panel uses). Same overlay family as the party
// panel. Chosen over deep-opening the Costs panel because that panel only
// carries the costs scan; the summary scan findings had no surface at all.
export default function QaFindingsPanel({
  sections,
  onAction,
  onClose,
}: {
  sections: QaSection[];
  onAction: (
    target: QaSection["target"],
    findingId: string,
    kind: FindingActionKind,
    note?: string,
  ) => void;
  onClose: () => void;
}) {
  return (
    <div className="quote-context-overlay" role="dialog" aria-label="Review before export">
      <div className="quote-context-panel party-panel qa-findings-panel">
        <div className="quote-context-head">
          <div>
            <strong>Review before export</strong>{" "}
            <span className="overview-muted">
              Automatic checks on the AI-written summaries. Reviewing them is
              required before you export.
            </span>
          </div>
          <div className="quote-context-actions">
            <button type="button" className="fact-source-link" onClick={onClose}>
              Close ✕
            </button>
          </div>
        </div>

        {sections.length === 0 && (
          <p className="overview-muted">All clear — nothing to review.</p>
        )}

        {sections.map((s) => {
          const actions = actionMap(s.validation);
          const notes = noteMap(s.validation);
          const rows = s.validation.issues
            .map((f, i) => ({ f, id: findingId(f, i) }))
            .filter(({ id }) => actions[id] !== "dismissed");
          const failsLeft = unaddressedFailCount(s.validation, actions);
          return (
            <section className="party-panel-section" key={s.target}>
              <h4 className="overview-section-title">
                {s.label}
                {failsLeft > 0 && (
                  <span className="overview-muted">
                    {" "}
                    · {failsLeft} to review
                  </span>
                )}
              </h4>
              {rows.length === 0 ? (
                <p className="overview-muted">All reviewed.</p>
              ) : (
                <ul className="findings-bar-list qa-findings-list">
                  {rows.map(({ f, id }) => (
                    <li key={id}>
                      <FindingMarker
                        finding={f}
                        action={actions[id]}
                        note={notes[id]}
                        onAction={(kind, note) => onAction(s.target, id, kind, note)}
                        inline={false}
                      />{" "}
                      {f.description}
                      <FindingContextCard finding={f} />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
